import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { ApiResponse, SeoConfig, WebsiteConfig, SeoFormValues, WebsiteFormValues, WebsiteStatus, MarkupFormValues, MarkupConfig } from "@/components/admin/configuration/schema"
import { toast } from "sonner"
import api from "@/app/api"

const queryConfig = {
  refetchOnWindowFocus: false,
  retry: 1,
  staleTime: 1000 * 60 * 30, // 30 minutes
  cacheTime: 1000 * 60 * 60, // 1 hour
}

// Type for direct API response
export interface WebsiteConfigResponse {
  status: WebsiteStatus;
  address: string;
  gstNumber: string;
  businessName: string;
  notifyEmailAddress: string;
  notifyMobileNumber: string;
  msmeNumber:string;
  cinNumber:string;
  panNumber:string;
  customerSupport: {
    mobile: string;
    email: string;
  };
  logo?: string;
  favIcon?: string;
  mode?:string;
}

export interface SeoConfigResponse {
  title: string;
  description: string;
  keywords: string;
  ogImage?: string;
}

interface ImageUploadResponse {
  url: string;
  message?: string;
}

interface MarkupConfigResponse {
  key: string;
  value: { international: number; domestic: number };
  title: string;
  description: string;
  createdAt: string;
  updatedAt: string;
}

export const useGetSeoConfig = () => {
  return useQuery<SeoFormValues, Error>({
    queryKey: ["seo-config"],
    queryFn: async () => {
      try {
        const response = await api.get<SeoConfigResponse>(`/configurations/seo`)
        if (!response.data) {
          throw new Error("No SEO configuration data received")
        }
        return response.data
      } catch (error: any) {
        console.error('SEO config error:', error)
        throw new Error(error.response?.data?.message || "Failed to fetch SEO configuration")
      }
    },
    ...queryConfig
  })
}

export const useUpdateSeoConfig = () => {
  const queryClient = useQueryClient()
  
  return useMutation<SeoConfigResponse, Error, SeoFormValues>({
    mutationFn: async (data) => {
      const response = await api.post<SeoConfigResponse>(`/configurations/seo`, data)
      if (!response.data) {
        throw new Error("Failed to update SEO configuration")
      }
      return response.data
    },
    onSuccess: () => {
      toast.success("SEO configuration updated successfully")
      queryClient.invalidateQueries({ queryKey: ['seo-config'] })
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })
}

export const useGetWebsiteConfig = () => {
  return useQuery<WebsiteConfigResponse, Error>({
    queryKey: ["website-config"],
    queryFn: async () => {
      try {
        const response = await api.get<WebsiteConfigResponse>(`/configurations/website`)
        const seoResponse = await api.get<SeoConfigResponse>(`/configurations/seo`)
        console.log('Website config response:', response.data)
        
        if (!response.data) {
          throw new Error("No website configuration data received")
        }
        
        return {...response.data, ...seoResponse.data}
      } catch (error: any) {
        console.error('Website config error:', error)
        throw new Error(error.response?.data?.message || "Failed to fetch website configuration")
      }
    },
    ...queryConfig
  })
}

export const useUpdateWebsiteConfig = () => {
  const queryClient = useQueryClient()
  
  return useMutation<WebsiteConfigResponse, Error, WebsiteFormValues>({
    mutationFn: async (data) => {
      const response = await api.post<WebsiteConfigResponse>(`/configurations/website`, data)
      if (!response.data) {
        throw new Error("Failed to update website configuration")
      }
      return response.data
    },
    onSuccess: () => {
      toast.success("Website Configuration Updated Successfully")
      queryClient.invalidateQueries({ queryKey: ['website-config'] })
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })
}

export const useGetMarkupConfig = () => {
  return useQuery<MarkupFormValues, Error>({
    queryKey: ["markup-config"],
    queryFn: async () => {
      try {
        const [hotelResponse, flightResponse] = await Promise.all([
          api.get<MarkupConfigResponse>('/configurations/markup/hotel'),
          api.get<MarkupConfigResponse>('/configurations/markup/flight')
        ])
        
        if (!hotelResponse.data || !flightResponse.data) {
          throw new Error("Failed to fetch markup configuration")
        }

        return {
          hotelMarkup: hotelResponse.data.value,
          flightMarkup: flightResponse.data.value
        }
      } catch (error: any) {
        console.error('Markup config error:', error)
        throw new Error(error.response?.data?.message || "Failed to fetch markup configuration")
      }
    },
    ...queryConfig
  })
}

export const useUpdateMarkupConfig = () => {
  const queryClient = useQueryClient()

  return useMutation<void, Error, { type: 'hotel' | 'flight', data: MarkupConfig }>({
    mutationFn: async ({ type, data }) => {
      await api.post(`/configurations/markup/${type}`, data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["markup-config"] })
      toast.success("Cancellation Service Charge Updated Successfully")
    },
    onError: (error) => {
      toast.error(error.message || "Failed To Update Cancellation Service Charge")
    }
  })
}

export const useUploadWebsiteImage = (imageType: 'logo' | 'favicon' | 'ogImage') => {
  const queryClient = useQueryClient()

  return useMutation<ImageUploadResponse, Error, File>({
    mutationFn: async (file) => {
      try {
        if (!file.type.startsWith('image/')) {
          throw new Error('Please upload an image file')
        }

        const maxSize = 5 * 1024 * 1024 // 5MB
        if (file.size > maxSize) {
          throw new Error('Image size should be less than 5MB')
        }

        const formData = new FormData()
        formData.append('file', file) // Changed 'file' to 'image' to match backend expectation
        
        const response = await api.post<ImageUploadResponse>(
          `/configurations/upload/${imageType}`, 
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data', // Explicitly set content type
            },
          }
        )

        if (!response.data) {
          throw new Error(`Failed to upload ${imageType}`)
        }
        return response.data
      } catch (error: any) {
        console.error(`Image upload error (${imageType}):`, error)
        throw new Error(error.response?.data?.message || `Failed to upload ${imageType}`)
      }
    },
    onSuccess: () => {
      toast.success(`${imageType} uploaded successfully`)
      
      // Invalidate relevant queries based on image type
      if (imageType === 'ogImage') {
        queryClient.invalidateQueries({ queryKey: ['seo-config'] })
      } else {
        queryClient.invalidateQueries({ queryKey: ['website-config'] })
      }
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })
}
